import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Terminal, Code2, Bot, ArrowRight, Zap, Volume2, Copy, FileText, Plug } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.15, duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] as const },
  }),
};

const stagger = {
  visible: { transition: { staggerChildren: 0.12 } },
  hidden: {},
};

function Section({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.section
      ref={ref}
      initial="hidden"
      animate={inView ? 'visible' : 'hidden'}
      variants={stagger}
      className={className}
    >
      {children}
    </motion.section>
  );
}

function FadeUp({ children, index = 0, className = '' }: { children: React.ReactNode; index?: number; className?: string }) {
  return (
    <motion.div variants={fadeUp} custom={index} className={className}>
      {children}
    </motion.div>
  );
}

export default function Skills() {
  const { t } = useTranslation('skills');

  const agents = [
    {
      icon: Terminal,
      name: t('integrations.claudeCode.name'),
      desc: t('integrations.claudeCode.desc'),
      install: t('integrations.claudeCode.install'),
    },
    {
      icon: Code2,
      name: t('integrations.vscodeCopilot.name'),
      desc: t('integrations.vscodeCopilot.desc'),
      install: t('integrations.vscodeCopilot.install'),
    },
    {
      icon: Bot,
      name: t('integrations.openaiCodex.name'),
      desc: t('integrations.openaiCodex.desc'),
      install: t('integrations.openaiCodex.install'),
    },
  ];

  const capabilities = [
    { icon: Volume2, label: t('capabilities.voiceDesign.label'), desc: t('capabilities.voiceDesign.desc') },
    { icon: Copy, label: t('capabilities.voiceClone.label'), desc: t('capabilities.voiceClone.desc') },
    { icon: FileText, label: t('capabilities.transcribe.label'), desc: t('capabilities.transcribe.desc') },
    { icon: Plug, label: t('capabilities.apiIntegration.label'), desc: t('capabilities.apiIntegration.desc') },
  ];

  return (
    <div className="bg-white min-h-screen">
      <Navbar />

      {/* Hero */}
      <div className="pt-32 pb-16 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-blue-200 bg-blue-50 mb-6">
              <Zap className="w-3.5 h-3.5 text-blue-600" />
              <span className="text-xs font-medium text-blue-700 tracking-wide uppercase">{t('hero.badge')}</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 tracking-tight max-w-3xl">
              {t('hero.title')}
            </h1>
            <p className="mt-6 text-gray-500 text-lg max-w-2xl leading-relaxed">
              {t('hero.description')}
            </p>
          </motion.div>
        </div>
      </div>

      {/* Supported capabilities */}
      <Section className="pb-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {capabilities.map((cap, i) => (
              <FadeUp key={cap.label} index={i}>
                <div className="flex items-center gap-3 p-4 rounded-xl bg-gray-50 border border-gray-100">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <cap.icon className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{cap.label}</p>
                    <p className="text-xs text-gray-400">{cap.desc}</p>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </Section>

      {/* Agent integrations */}
      <Section className="pb-28 sm:pb-36 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20">
          <FadeUp>
            <p className="text-blue-600 text-sm font-semibold tracking-widest uppercase mb-3">{t('integrations.subtitle')}</p>
          </FadeUp>
          <FadeUp index={1}>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">
              {t('integrations.title')}
            </h2>
          </FadeUp>

          <div className="mt-16 grid sm:grid-cols-3 gap-6">
            {agents.map((a, i) => (
              <FadeUp key={a.name} index={i}>
                <div className="group relative p-8 rounded-2xl border border-gray-200 bg-white hover:border-blue-200 hover:shadow-lg hover:shadow-blue-500/5 transition-all duration-500">
                  <a.icon className="w-8 h-8 text-blue-600 mb-5" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{a.name}</h3>
                  <p className="text-gray-500 text-sm mb-5 leading-relaxed">{a.desc}</p>
                  <p className="text-gray-400 text-xs font-medium uppercase tracking-wider mb-3">{t('integrations.installLabel')}</p>
                  <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-gray-50 border border-gray-100 font-mono text-xs text-gray-500 break-all">
                    {a.install}
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </Section>

      {/* How it works */}
      <Section className="py-28 sm:py-36 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20">
          <FadeUp>
            <p className="text-blue-600 text-sm font-semibold tracking-widest uppercase mb-3">{t('howItWorks.subtitle')}</p>
          </FadeUp>
          <FadeUp index={1}>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">
              {t('howItWorks.title')}
            </h2>
          </FadeUp>

          <div className="mt-16 grid md:grid-cols-3 gap-8">
            {[
              { step: '01', title: t('howItWorks.step1.title'), desc: t('howItWorks.step1.desc') },
              { step: '02', title: t('howItWorks.step2.title'), desc: t('howItWorks.step2.desc') },
              { step: '03', title: t('howItWorks.step3.title'), desc: t('howItWorks.step3.desc') },
            ].map((item, i) => (
              <FadeUp key={item.step} index={i}>
                <div className="relative">
                  <span className="text-6xl font-bold text-gray-100">{item.step}</span>
                  <h3 className="mt-2 text-xl font-semibold text-gray-900">{item.title}</h3>
                  <p className="mt-2 text-gray-500 leading-relaxed">{item.desc}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </Section>

      {/* CTA */}
      <Section className="py-28 sm:py-36 bg-gradient-to-br from-blue-600 to-blue-700">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 text-center">
          <FadeUp>
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">
              {t('cta.title')}
            </h2>
          </FadeUp>
          <FadeUp index={1}>
            <p className="mt-6 text-blue-100 text-lg max-w-xl mx-auto">
              {t('cta.description')}
            </p>
          </FadeUp>
          <FadeUp index={2}>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <a
                href="https://github.com/shuzibabay-aigc/aivoice-skill"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-2 px-8 py-4 bg-white hover:bg-gray-50 text-blue-600 font-semibold rounded-xl transition-all duration-300 hover:shadow-lg text-lg"
              >
                {t('cta.github')}
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </a>
              <a
                href="/register"
                className="inline-flex items-center gap-2 px-8 py-4 border border-white/30 hover:border-white/50 text-white font-medium rounded-xl transition-all duration-300 hover:bg-white/10 text-lg"
              >
                {t('cta.register')}
              </a>
            </div>
          </FadeUp>
        </div>
      </Section>

      {/* Footer */}
      <footer className="py-12 bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-gray-400 text-sm">&copy; {new Date().getFullYear()} {t('footer.copyright')}</p>
          <div className="flex items-center gap-6">
            <a href="/api-docs" className="text-gray-400 hover:text-gray-600 text-sm transition-colors">
              {t('footer.apiDocs')}
            </a>
            <a href="https://github.com/shuzibabay-aigc/aivoice-skill" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-gray-600 text-sm transition-colors">
              {t('footer.github')}
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
